import { useEffect, useState } from 'react';
import { Globe, ShoppingBag } from 'lucide-react';
import { api } from '../lib/api.js';
import { useWorkspace } from '../lib/workspace.jsx';

const PLATFORMS = [
  {
    key: 'web',
    label: 'Web App',
    Icon: Globe,
    hint: 'A site or admin you host yourself.',
  },
  {
    key: 'shopify',
    label: 'Shopify App',
    Icon: ShoppingBag,
    hint: 'An app embedded in the Shopify admin.',
  },
];

/**
 * Where this app's widget runs.
 *
 * Deliberately says nothing about *which* store. A tenant here is the
 * publisher of an app, and their app is installed by thousands of merchants —
 * so there is no one store at this level, and asking for one could only ever
 * record a single arbitrary user of it. Which merchant a conversation belongs
 * to arrives per conversation, as visitor metadata, and shows in the context
 * panel beside the thread.
 *
 * What the choice does change is the install advice opposite: an embedded
 * Shopify app is served from the publisher's own domain rather than
 * `admin.shopify.com`, it runs in a nested iframe that cannot read the admin
 * URL above it, and the fields worth passing about each merchant differ.
 *
 * Offered here as well as at creation because a publisher ships a web app
 * first and an embedded one later, and every tenant created before the
 * distinction existed is a web app whether or not that is true.
 */
export function AppTypeCard({ canEdit }) {
  const { activeApp, activeAppId, refreshApps } = useWorkspace();

  const [platform, setPlatform] = useState(activeApp?.platform ?? 'web');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  /* A different tenant is a different question; nothing carries over. */
  useEffect(() => {
    setError(null);
    setSaved(false);
  }, [activeAppId]);

  /* Follows what the server holds, including right after a save. */
  useEffect(() => {
    setPlatform(activeApp?.platform ?? 'web');
  }, [activeApp?.platform]);

  if (!activeApp) return null;

  const current = activeApp.platform ?? 'web';
  const changed = platform !== current;

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await api.apps.update(activeAppId, { platform });
      await refreshApps();
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="card__head">
        <span className="card__title">App type</span>
        {saved && !changed && <span className="card__meta">Saved</span>}
      </div>

      <div className="card__body">
        <div className="choice">
          {PLATFORMS.map(({ key, label, Icon, hint }) => (
            <button
              type="button"
              key={key}
              className={`choice__option${platform === key ? ' is-active' : ''}`}
              onClick={() => canEdit && setPlatform(key)}
              aria-pressed={platform === key}
              disabled={!canEdit}
            >
              <Icon size={14} className="choice__icon" />
              <span className="choice__label">{label}</span>
              <span className="choice__hint">{hint}</span>
            </button>
          ))}
        </div>

        {error && <div className="login__error">{error}</div>}
      </div>

      {canEdit && (
        <div className="card__actions">
          <button
            type="button"
            className="button button--primary"
            onClick={save}
            disabled={saving || !changed}
          >
            {saving ? 'Saving…' : 'Save app type'}
          </button>
        </div>
      )}

      <div className="card__foot">
        {platform === 'shopify' ? (
          <>
            One app, every store that installs it. Allow your own app domain here — not{' '}
            <code>admin.shopify.com</code>, which is the frame your app is embedded in rather than
            the one the widget runs in. Pass each merchant&apos;s shop domain as visitor metadata
            so an agent can tell which store they are talking to.
          </>
        ) : (
          <>
            Anything you host yourself. Switch to Shopify for install guidance shaped around an
            embedded app — a nested iframe that cannot read the admin URL above it.
          </>
        )}
      </div>
    </div>
  );
}

export default AppTypeCard;
